import React, { useMemo, useState, useEffect } from "react";
import { Save, X, Printer } from "lucide-react";
import { printMovement } from "../print/printMovement";

export function SubWarehouseConsumptionModule({ materials, warehouses = [], people = [], onRegister }) {
  const subWarehouses = useMemo(() => (warehouses || []).filter((w) => !!w.parent_id), [warehouses]);

  const [warehouseId, setWarehouseId] = useState(subWarehouses[0]?.id || '');
  const [materialId, setMaterialId] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [responsibleId, setResponsibleId] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [municipio, setMunicipio] = useState('');
  const [observations, setObservations] = useState('');
  const [statusMsg, setStatusMsg] = useState(null);
  const [lastReceipt, setLastReceipt] = useState(null);

  useEffect(() => {
    if (!warehouseId && subWarehouses[0]?.id) setWarehouseId(subWarehouses[0].id);
  }, [subWarehouses, warehouseId]);

  const filteredMaterials = useMemo(() => {
    if (!warehouseId) return [];
    return materials.filter((m) => {
      const qty = m.stockByWarehouse?.[warehouseId];
      return qty && Number(qty) > 0;
    }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [materials, warehouseId]);

  useEffect(() => {
    if (materialId && !filteredMaterials.find((m) => String(m.id) === String(materialId))) {
      setMaterialId('');
    }
  }, [warehouseId, filteredMaterials, materialId]);

  const canSubmit = useMemo(() => {
    return warehouseId && materialId && Number(quantity) > 0 && responsibleId
      && String(referenceNumber).trim() && String(municipio).trim();
  }, [warehouseId, materialId, quantity, responsibleId, referenceNumber, municipio]);

  const handleCancel = () => {
    setMaterialId('');
    setQuantity(0);
    setResponsibleId('');
    setReferenceNumber('');
    setMunicipio('');
    setObservations('');
    setStatusMsg(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const responsible = people.find((p) => String(p.id) === String(responsibleId));
    const material = materials.find((m) => String(m.id) === String(materialId));
    const wh = subWarehouses.find((w) => String(w.id) === String(warehouseId));

    const item = {
      exitType: 'Consumible',
      warehouseId,
      materialId,
      quantity,
      reason: 'Consumo sub-bodega',
      observations,
      responsibleId,
      responsibleName: responsible?.name || '',
      responsibleDoc: responsible?.dni || responsible?.id || '',
      referenceNumber: String(referenceNumber).trim(),
      municipio: String(municipio).trim(),
    };

    const res = await onRegister(item);
    setStatusMsg(res?.message || (res?.success ? 'Consumo registrado' : 'No fue posible registrar el consumo'));
    if (res?.success) {
      const consecutivo = res?.consecutivo || '';
      try {
        const receipt = {
          type: 'Consumible',
          date: new Date().toLocaleString('es-CO', { timeZone: 'America/Bogota' }),
          materialName: material?.name || String(materialId),
          materialCode: material?.code,
          materialUnit: material?.unit,
          category: material?.category || material?.type || 'Material',
          quantity: parseInt(quantity),
          observations: `Obra/caso/ticket/consigna: ${item.referenceNumber} — Municipio: ${item.municipio}${observations ? ' — ' + observations : ''}`,
          warehouseId,
          warehouseName: wh?.name,
          responsibleDoc: item.responsibleDoc || 'Sistema',
          responsibleName: item.responsibleName || '',
          entryRef: consecutivo,
          consecutivo,
        };
        setLastReceipt(receipt);
        printMovement(receipt);
      } catch (_) {}
      handleCancel();
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="section-title mb-6">Consumos (sub-bodegas)</h3>

      {subWarehouses.length === 0 && (
        <p className="text-sm text-gray-600 mb-4">
          No hay sub-bodegas configuradas todavía. Crea una desde "Configurar Bodegas" eligiendo una bodega padre (ej. Línea viva centro).
        </p>
      )}

      {lastReceipt && (
        <div className="mb-4 p-3 border rounded bg-gray-50 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold">Consumo registrado{lastReceipt.consecutivo ? ` — ${lastReceipt.consecutivo}` : ''}</p>
            <p className="text-xs text-gray-600">{lastReceipt.materialName} — Cant: {lastReceipt.quantity} — {lastReceipt.date}</p>
          </div>
          <button type="button" onClick={() => printMovement(lastReceipt)} className="bg-gray-800 text-white px-3 py-2 rounded flex items-center gap-2">
            <Printer size={16} /> Imprimir comprobante
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sub-bodega *</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
            >
              <option value="">Seleccione...</option>
              {subWarehouses.map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Material *</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              value={materialId}
              onChange={(e) => setMaterialId(e.target.value)}
            >
              <option value="">Seleccione...</option>
              {filteredMaterials.map((m) => {
                const stock = m.stockByWarehouse?.[warehouseId] || 0;
                return (
                  <option key={m.id} value={m.id}>
                    {m.code ? `${m.code} - ${m.name} (Stock: ${stock})` : `${m.id} - ${m.name} (Stock: ${stock})`}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cantidad *</label>
            <input
              type="number"
              min={0}
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="0"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Responsable *</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              value={responsibleId}
              onChange={(e) => setResponsibleId(e.target.value)}
            >
              <option value="">Seleccione responsable...</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">N° de obra / caso / ticket / consigna *</label>
            <input
              type="text"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Ej: T-12345"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Municipio *</label>
            <input
              type="text"
              value={municipio}
              onChange={(e) => setMunicipio(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Ej: Popayán"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Observaciones</label>
          <textarea
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
            placeholder="Detalles adicionales..."
          />
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="inline-flex items-center gap-2 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg"
          >
            <X className="h-4 w-4" /> Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> Guardar
          </button>
        </div>

        {statusMsg && (
          <p className="text-sm text-gray-600 pt-2">{statusMsg}</p>
        )}
      </form>
    </div>
  );
}
